import { texToPlainText } from './clipboard-from-rendered';

function closestKatex(node: Node): Element | null {
  const element = node instanceof Element ? node : node.parentElement;
  return element?.closest('.katex') ?? null;
}

/**
 * When the user selects and copies chat content, put plain-text formulas on the
 * clipboard instead of KaTeX glyphs or raw LaTeX.
 */
export function installPlainMathCopyHandler(): () => void {
  const onCopy = (event: ClipboardEvent) => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !event.clipboardData) return;

    const range = selection.getRangeAt(0).cloneRange();
    const startKatex = closestKatex(range.startContainer);
    if (startKatex) range.setStartBefore(startKatex);
    const endKatex = closestKatex(range.endContainer);
    if (endKatex) range.setEndAfter(endKatex);

    const fragment = range.cloneContents();
    if (!fragment.querySelector('.katex-mathml')) return;

    const walkerRoot = document.createElement('div');
    walkerRoot.appendChild(fragment);

    walkerRoot.querySelectorAll('.katex-mathml').forEach((mathml) => {
      const annotation = mathml.querySelector('annotation');
      const tex = (annotation?.textContent ?? '').trim();
      const plain = tex ? texToPlainText(tex) : '';
      if (!plain) return;
      const target = mathml.closest('.katex-display') ?? mathml.closest('.katex') ?? mathml;
      const isDisplay = Boolean(mathml.closest('.katex-display'));
      target.replaceWith(document.createTextNode(isDisplay ? `\n${plain}\n` : plain));
    });

    const plain = (walkerRoot.innerText || walkerRoot.textContent || '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    if (!plain) return;

    event.clipboardData.setData('text/plain', plain);
    event.clipboardData.setData('text/html', walkerRoot.innerHTML);
    event.preventDefault();
  };

  document.addEventListener('copy', onCopy);
  return () => document.removeEventListener('copy', onCopy);
}

let uninstall: (() => void) | null = null;

/** Idempotent install for chat surfaces (safe to call from multiple mounts). */
export function ensurePlainMathCopyHandler(): void {
  if (typeof document === 'undefined' || uninstall) return;
  uninstall = installPlainMathCopyHandler();
}
